import type {
  CoreAdminProps,
  AuthProvider,
  DashboardComponent,
  LayoutComponent,
} from "ra-core";
import { CustomRoutes, localStorageStore, Resource } from "ra-core";
import { useEffect, useMemo } from "react";
import { Route } from "react-router";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { createAsyncStoragePersister } from "@tanstack/query-async-storage-persister";
import { Admin } from "@/components/admin/admin";

import companies from "../companies";
import contacts from "../contacts";
import { Dashboard } from "../dashboard/Dashboard";
import { MobileDashboard } from "../dashboard/MobileDashboard";
import sponsorings from "../sponsorings";
import { AccessDenied } from "../layout/AccessDenied";
import { AuthenticationError } from "../layout/AuthenticationError";
import { Layout } from "../layout/Layout";
import { MobileLayout } from "../layout/MobileLayout";
import { LoginPage } from "../login/LoginPage";
import sales from "../sales";
import contractTypes from "../contractTypes";
import { SettingsPageMobile } from "../settings/SettingsPageMobile";
import { SettingsFasenMobile } from "../settings/SettingsFasenMobile";
import { ProfilePage } from "../settings/ProfilePage";
import { SettingsPage } from "../settings/SettingsPage";
import { SalesListMobile } from "../sales/SalesList";
import { ContractTypeListMobile } from "../contractTypes/ContractTypeList";
import {
  CONFIGURATION_STORE_KEY,
  type ConfigurationContextValue,
} from "./ConfigurationContext";
import type { CrmDataProvider } from "../providers/types";
import {
  defaultLogo,
  defaultSponsoringActiveStatuses,
  defaultSponsoringClosedStatuses,
  defaultSponsoringStages,
  defaultTitle,
} from "./defaultConfiguration";
import { i18nProvider as defaulti18nProvider } from "../providers/commons/i18nProvider";
import { useIsMobile } from "@/hooks/use-mobile.ts";
import { ContactListMobile } from "../contacts/ContactList.tsx";
import { ContactShow } from "../contacts/ContactShow.tsx";
import { CompanyListMobile } from "../companies/CompanyList.tsx";
import { CompanyShow } from "../companies/CompanyShow.tsx";
import { SponsoringListMobile } from "../sponsorings/SponsoringListMobile.tsx";
import { SponsoringShowMobile } from "../sponsorings/SponsoringShowMobile.tsx";
import { SponsoringViewPage } from "../sponsorings/SponsoringViewPage.tsx";
import { NoteShowPage } from "../notes/NoteShowPage.tsx";

const defaultStore = localStorageStore(undefined, "CRM");

export type CRMProps = {
  /** The production entry passes the PHP providers, the demo the FakeRest ones. */
  dataProvider: CrmDataProvider;
  authProvider: AuthProvider;
  i18nProvider?: CoreAdminProps["i18nProvider"];
  disableTelemetry?: boolean;
  store?: CoreAdminProps["store"];
  dashboard?: DashboardComponent;
  layout?: LayoutComponent;
} & Partial<ConfigurationContextValue>;

/**
 * CRM Component
 *
 * Sets up and renders the CRM application using `ra-core`. Configuration
 * props (stages, title, logo) seed the store on first load; admins can edit
 * them later on the settings page.
 *
 * @param {SponsoringStage[]} sponsoringStages - The stages of sponsorings used in the application.
 * @param {string[]} sponsoringActiveStatuses - The stages that count as a running sponsorship.
 * @param {string[]} sponsoringClosedStatuses - The stages that are closed (never shown on the board).
 * @param {string} title - The title of the CRM application.
 * @param {string} logo - The colour lockup. Must be an imported asset, an absolute URL, or a data URI.
 */
export const CRM = ({
  sponsoringStages = defaultSponsoringStages,
  sponsoringActiveStatuses = defaultSponsoringActiveStatuses,
  sponsoringClosedStatuses = defaultSponsoringClosedStatuses,
  title = defaultTitle,
  logo = defaultLogo,
  dataProvider,
  authProvider,
  i18nProvider = defaulti18nProvider,
  store = defaultStore,
  ...rest
}: CRMProps) => {
  // Seed the store with CRM prop values if not already stored
  useEffect(() => {
    if (!store.getItem(CONFIGURATION_STORE_KEY)) {
      store.setItem(CONFIGURATION_STORE_KEY, {
        sponsoringStages,
        sponsoringActiveStatuses,
        sponsoringClosedStatuses,
        title,
        logo,
      } satisfies ConfigurationContextValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store]);

  const isMobile = useIsMobile();

  // on login, pre-fetch the configuration to avoid a flickering
  // when accessing the app for the first time
  const wrappedAuthProvider = useMemo<AuthProvider>(
    () => ({
      ...authProvider,
      login: async (params: any) => {
        const result = await authProvider.login(params);
        try {
          const config = await dataProvider.getConfiguration();
          if (Object.keys(config).length > 0) {
            store.setItem(CONFIGURATION_STORE_KEY, config);
          }
        } catch {
          // Non-critical: config will load via useConfigurationLoader
        }
        return result;
      },
      logout: async (params: any) => {
        try {
          store.removeItem(CONFIGURATION_STORE_KEY);
        } catch {
          // Ignore
        }
        return authProvider.logout(params);
      },
    }),
    [authProvider, dataProvider, store],
  );

  const ResponsiveAdmin = isMobile ? MobileAdmin : DesktopAdmin;

  return (
    <ResponsiveAdmin
      dataProvider={dataProvider}
      authProvider={wrappedAuthProvider}
      i18nProvider={i18nProvider}
      store={store}
      loginPage={LoginPage}
      accessDenied={AccessDenied}
      authenticationError={AuthenticationError}
      requireAuth
      disableTelemetry
      {...rest}
    />
  );
};

const DesktopAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  return (
    <Admin
      layout={props.layout ?? Layout}
      dashboard={props.dashboard ?? Dashboard}
      {...props}
    >
      <CustomRoutes>
        <Route path={ProfilePage.path} element={<ProfilePage />} />
        <Route path={SettingsPage.path} element={<SettingsPage />} />
        {/* Two more views on the sponsorings resource, each with its own page */}
        <Route
          path="/balsponsoren/*"
          element={<SponsoringViewPage view="ball" />}
        />
        <Route path="/leads/*" element={<SponsoringViewPage view="leads" />} />
      </CustomRoutes>
      <Resource name="sponsorings" {...sponsorings} />
      <Resource name="contacts" {...contacts} />
      <Resource name="companies" {...companies} />
      <Resource name="contact_notes" />
      <Resource name="sponsoring_notes" />
      <Resource
        name="contract_types"
        {...contractTypes}
        recordRepresentation="name"
      />
      <Resource name="sales" {...sales} />
    </Admin>
  );
};

const MobileAdmin = (
  props: CoreAdminProps & {
    dashboard?: DashboardComponent;
    layout?: LayoutComponent;
  },
) => {
  const queryClient = new QueryClient({
    defaultOptions: {
      queries: {
        gcTime: 1000 * 60 * 60 * 24, // 24 hours
        networkMode: "offlineFirst",
      },
      mutations: {
        networkMode: "offlineFirst",
      },
    },
  });
  const asyncStoragePersister = createAsyncStoragePersister({
    storage: localStorage,
  });

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: asyncStoragePersister }}
    >
      <Admin
        queryClient={queryClient}
        layout={props.layout ?? MobileLayout}
        dashboard={props.dashboard ?? MobileDashboard}
        {...props}
      >
        <CustomRoutes>
          <Route path={ProfilePage.path} element={<ProfilePage />} />
          <Route
            path={SettingsPageMobile.path}
            element={<SettingsPageMobile />}
          />
          <Route
            path={SettingsFasenMobile.path}
            element={<SettingsFasenMobile />}
          />
          {/* Two more views on the sponsorings resource, each with its own page */}
          <Route
            path="/balsponsoren/*"
            element={<SponsoringViewPage view="ball" />}
          />
          <Route
            path="/leads/*"
            element={<SponsoringViewPage view="leads" />}
          />
        </CustomRoutes>
        <Resource
          name="contacts"
          list={ContactListMobile}
          show={ContactShow}
          create={contacts.create}
          edit={contacts.edit}
          recordRepresentation={contacts.recordRepresentation}
        >
          <Route path=":id/notes/:noteId" element={<NoteShowPage />} />
        </Resource>
        <Resource
          name="companies"
          list={CompanyListMobile}
          show={CompanyShow}
          create={companies.create}
          edit={companies.edit}
          recordRepresentation={companies.recordRepresentation}
        />
        <Resource
          name="sponsorings"
          list={SponsoringListMobile}
          show={SponsoringShowMobile}
          hasCreate
          hasEdit
          recordRepresentation={sponsorings.recordRepresentation}
        />
        <Resource name="contact_notes" />
        <Resource name="sponsoring_notes" />
        <Resource
          name="contract_types"
          list={ContractTypeListMobile}
          create={contractTypes.create}
          edit={contractTypes.edit}
          recordRepresentation="name"
        />
        <Resource
          name="sales"
          list={SalesListMobile}
          create={sales.create}
          edit={sales.edit}
          recordRepresentation={sales.recordRepresentation}
        />
      </Admin>
    </PersistQueryClientProvider>
  );
};
